import {
	alpha,
	ButtonBase,
	lighten,
	Stack,
	Typography,
	useTheme,
} from "@mui/material";
import { Calculator, ChevronDown, ChevronUp } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { type Control, useController } from "react-hook-form";
import type { GoalFormModel } from "web_myplann/Goals/Forms/useGoalForm";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { FlatRowInput } from "web_myplann/Shared/Components/FlatRow/FlatRowInput";
import { formatThousands, parseThousands } from "web_myplann/Utils/formatters";

type Props = {
	control: Control<GoalFormModel>;
	useRentCalc: boolean;
	onToggle: (next: boolean) => void;
};

const computeRentTotal = (monthly: number, years: number): number =>
	monthly * 12 * years;

export const RentCalculator = ({ control, useRentCalc, onToggle }: Props) => {
	const theme = useTheme();
	const { t } = useResource();

	const { field } = useController({ control, name: "totalValue" });
	const onChangeRef = useRef(field.onChange);
	onChangeRef.current = field.onChange;

	const [monthlyAmount, setMonthlyAmount] = useState<number | undefined>(
		undefined,
	);
	const [years, setYears] = useState<number | undefined>(undefined);

	const rentTotal =
		useRentCalc && monthlyAmount && years
			? computeRentTotal(monthlyAmount, years)
			: 0;

	useEffect(() => {
		if (!useRentCalc) return;
		onChangeRef.current(rentTotal > 0 ? rentTotal : undefined);
	}, [rentTotal, useRentCalc]);

	const ChevronIcon = useRentCalc ? ChevronUp : ChevronDown;
	const monthlyUnit = t(Resources.Goals.Form.MonthlyProjection.monthlyUnit);
	const yearsAbbr = t(Resources.Goals.Form.MonthlyProjection.yearsAbbr);

	return (
		<>
			<ButtonBase
				onClick={() => onToggle(!useRentCalc)}
				sx={{
					width: "100%",
					px: 2,
					py: 1.25,
					gap: 1,
					justifyContent: "flex-start",
					borderBottom: `1px solid ${alpha(theme.palette.grey[100], 0.6)}`,
					"&:active": { bgcolor: theme.palette.grey[50] },
				}}
			>
				<Calculator size={16} color={theme.palette.primary.main} />
				<Typography
					fontSize={12}
					fontWeight={500}
					color={theme.palette.primary.main}
				>
					{useRentCalc
						? t(Resources.Goals.Form.RentCalc.disableButton)
						: t(Resources.Goals.Form.RentCalc.enableButton)}
				</Typography>
				<ChevronIcon
					size={14}
					color={theme.palette.primary.main}
					style={{ marginLeft: "auto" }}
				/>
			</ButtonBase>

			{useRentCalc && (
				<Stack
					sx={{
						bgcolor: alpha(lighten(theme.palette.primary.main, 0.9), 0.3),
					}}
				>
					<FlatRowInput
						label={t(Resources.Goals.Form.RentCalc.monthlyLabel)}
						value={
							monthlyAmount == null
								? ""
								: formatThousands(String(monthlyAmount))
						}
						onChange={(next) => setMonthlyAmount(parseThousands(next))}
						suffix={t(Resources.Goals.Amount.currencySign)}
						placeholder={t(Resources.Goals.Form.RentCalc.monthlyPlaceholder)}
						type="text"
						inputMode="numeric"
					/>
					<FlatRowInput
						label={t(Resources.Goals.Form.RentCalc.durationLabel)}
						value={years == null ? "" : String(years)}
						onChange={(next) => {
							const digits = next.replace(/\D/g, "");
							setYears(digits ? Number(digits) : undefined);
						}}
						suffix={t(Resources.Goals.Form.RentCalc.durationSuffix)}
						placeholder={t(Resources.Goals.Form.RentCalc.durationPlaceholder)}
						type="text"
						inputMode="numeric"
						isLast
					/>
					{rentTotal > 0 && (
						<Stack
							sx={{
								px: 2,
								py: 1.5,
								borderTop: `1px solid ${alpha(theme.palette.grey[100], 0.6)}`,
							}}
						>
							<Stack
								direction="row"
								alignItems="center"
								justifyContent="space-between"
							>
								<Typography fontSize={14} color={theme.palette.grey[700]}>
									{t(Resources.Goals.Form.RentCalc.totalLabel)}
								</Typography>
								<Typography
									fontSize={14}
									fontWeight={700}
									color={theme.palette.grey[900]}
									sx={{ fontVariantNumeric: "tabular-nums" }}
								>
									{rentTotal.toLocaleString("cs-CZ")}{" "}
									{t(Resources.Goals.Amount.currencySign)}
								</Typography>
							</Stack>
							<Typography
								fontSize={11}
								color={theme.palette.grey[400]}
								sx={{ mt: 0.25 }}
							>
								{(monthlyAmount ?? 0).toLocaleString("cs-CZ")} {monthlyUnit} x{" "}
								{years ?? 0} {yearsAbbr}
							</Typography>
						</Stack>
					)}
				</Stack>
			)}
		</>
	);
};
